import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminBarChart } from "@/components/admin/bar-chart";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { KIND_META, marketingHubApi } from "@/lib/marketing/hub-api";

const nombre = (n: number) => n.toLocaleString("fr-FR");

const dateCourte = (iso: string) =>
	new Date(iso).toLocaleDateString("fr-FR", {
		weekday: "short",
		day: "2-digit",
		month: "2-digit",
	});

/**
 * Vue d'ensemble du module : ce que le contenu et les campagnes ont produit,
 * et ce qui part bientôt.
 *
 * Les quatre indicateurs de tête viennent de quatre modules différents
 * (bannières, Mag, newsletters, push) : c'est le seul écran qui les montre
 * côte à côte, et donc le seul où l'on puisse comparer un canal à un autre.
 */
export function AdminMarketingOverviewPage() {
	const { data, isLoading, isError } = useQuery({
		queryKey: ["marketing", "overview"],
		queryFn: () => marketingHubApi.overview(),
	});

	return (
		<>
			<PageHead
				title="Marketing & Contenus"
				sub="Performance du contenu et des campagnes"
			/>

			{isError && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="danger"
						title="Indicateurs indisponibles"
						description="Les chiffres n'ont pas pu être chargés. Les écrans du module restent accessibles."
					/>
				</div>
			)}

			{/* Un canal dont la chaîne technique est incomplète le dit ici : préparer
			    une campagne qui ne pourra pas partir est le pire des silences. */}
			{data && !data.readiness.push && (
				<div style={{ marginBottom: 12 }}>
					<AlertBanner
						variant="warning"
						title="Les notifications de l'app ne peuvent pas être expédiées"
						description="La clé de service FCM n'est pas configurée sur le serveur. Les campagnes se préparent et restent en attente."
					/>
				</div>
			)}
			{data && !data.readiness.whatsapp && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="warning"
						title="La connexion WhatsApp Business ne répond pas"
						description="Vérifiez le jeton Meta avant de programmer un envoi."
					/>
				</div>
			)}

			<div className="kpi-grid">
				<Kpi
					label="Bannières actives"
					value={isLoading ? "—" : nombre(data?.kpis.activeBanners ?? 0)}
					variant="accent"
					icon="layout"
					delta={
						data ? `CTR moyen ${data.kpis.bannerCtr.toFixed(1)} %` : undefined
					}
					deltaVariant="accent"
				/>
				<Kpi
					label="Articles publiés"
					value={isLoading ? "—" : nombre(data?.kpis.publishedArticles ?? 0)}
					variant="forest"
					icon="edit"
					delta={data ? `${nombre(data.kpis.articleViews)} vues` : undefined}
					deltaVariant="forest"
				/>
				<Kpi
					label="Abonnés newsletter"
					value={isLoading ? "—" : nombre(data?.kpis.subscribers ?? 0)}
					variant="ochre"
					icon="mail"
					delta="Ayant accepté les emails"
					deltaVariant="neutral"
				/>
				<Kpi
					label="Taux d'ouverture"
					value={
						isLoading ? (
							"—"
						) : (
							<>
								{(data?.kpis.openRate ?? 0).toFixed(1)}
								<small>%</small>
							</>
						)
					}
					icon="chart"
					delta="Moyenne des campagnes envoyées"
					deltaVariant="neutral"
				/>
			</div>

			<div className="grid-2">
				<Card>
					<CardHead
						title="Engagement par canal"
						sub="Ouverture pour l'email, clic pour le push et les bannières, remise pour WhatsApp"
					/>
					<CardBody>
						{data && data.channels.some((c) => c.engagement > 0) ? (
							<>
								<AdminBarChart
									data={data.channels.map((c) => c.engagement)}
									labels={data.channels.map((c) => c.label)}
									color="var(--a-terracotta)"
									height={150}
								/>
								<div
									className="muted"
									style={{ fontSize: 11, marginTop: 8, textAlign: "center" }}
								>
									Taux d'engagement par canal (%)
								</div>
							</>
						) : (
							<div className="empty">
								Aucune campagne mesurée pour l'instant. Les premiers chiffres
								apparaîtront après le premier envoi.
							</div>
						)}
					</CardBody>
				</Card>

				<Card>
					<CardHead
						title="Prochaines sorties"
						right={
							<Link className="btn btn-ghost btn-sm" to="/marketing/calendrier">
								Calendrier <AdminIcon n="chevR" s={12} />
							</Link>
						}
					/>
					<CardBody>
						{data && data.upcoming.length > 0 ? (
							data.upcoming.map((e, i) => {
								const meta = KIND_META[e.kind];
								return (
									<Link
										key={`${e.kind}-${e.id}`}
										to={meta.to}
										style={{
											display: "flex",
											alignItems: "center",
											gap: 12,
											padding: "10px 0",
											borderBottom:
												i < data.upcoming.length - 1
													? "1px solid var(--a-border)"
													: "none",
											color: "inherit",
											textDecoration: "none",
										}}
									>
										<span className={`cal-event ${meta.tone}`}>
											{meta.label}
										</span>
										<strong style={{ flex: 1, fontSize: 12.5 }}>
											{e.title}
										</strong>
										<span className="muted" style={{ fontSize: 11 }}>
											{dateCourte(e.at)}
										</span>
										<AdminIcon n="chevR" s={14} c="var(--a-ink-faint)" />
									</Link>
								);
							})
						) : (
							<div className="empty">
								Rien de programmé ce mois-ci.
							</div>
						)}
					</CardBody>
				</Card>
			</div>
		</>
	);
}
